import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ShieldCheck,
  Building2,
  Store,
  CheckCircle2,
  AlertCircle,
  Search,
  Mail,
  ArrowRight,
} from "lucide-react";
import { sellerAPI } from "../API/axios";

interface GSTResult {
  businessName: string;
  tradeName: string;
  gstStatus: "Active" | "Inactive" | string;
}

interface FormErrors {
  email?: string;
  gstin?: string;
}

const GSTPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", gstin: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GSTResult | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isGstVerified, setIsGstVerified] = useState(false);

  // Check if GST is already verified on component mount
  useEffect(() => {
    const sellerGstin = localStorage.getItem("sellerGstin");

    // If GSTIN exists and is not null/undefined/empty
    if (
      sellerGstin &&
      sellerGstin !== "null" &&
      sellerGstin !== "undefined" &&
      sellerGstin.trim() !== ""
    ) {
      setIsGstVerified(true);

      // Show success toast and redirect after 3 seconds
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-bold">GST Already Verified! ✅</span>
          <span className="text-sm">Redirecting to dashboard...</span>
        </div>,
        {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        },
      );

      // Redirect to home page after 3 seconds
      const timer = setTimeout(() => {
        navigate("/");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [navigate]);

  const validateGSTIN = (g: string) =>
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(g);

  const validate = () => {
    const e: FormErrors = {};
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      e.email = "Valid email required";
    if (!validateGSTIN(form.gstin.toUpperCase()))
      e.gstin = "Invalid GSTIN format (e.g. 27AABPT1653R1Z3)";
    setErrors(e);
    return Object.keys(e)?.length === 0;
  };

  const handleVerify = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await sellerAPI.verifyGSTIN({
        email: form.email,
        gstin: form.gstin.toUpperCase(),
      });

      setResult(res.data);

      // After successful verification, save GST to localStorage
      localStorage.setItem("sellerGstin", form.gstin.toUpperCase());

      // toast.success(
      //   <div className="flex flex-col gap-1">
      //     <span className="font-bold">GST verified successfully! ✅</span>
      //     <span className="text-sm">Redirecting to dashboard...</span>
      //   </div>,
      //   {
      //     position: "top-center",
      //     autoClose: 3000,
      //   },
      // );

      // Redirect to home after successful verification
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "GST verification failed.");
    } finally {
      setLoading(false);
    }
  };

  // If GST is already verified, show a message instead of the form
  if (isGstVerified) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        {/* Modal Content */}
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-slideUp border border-green-100">
          <div className="text-center">
            {/* Success Icon */}
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-green-600" />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              GST Already Verified! 🎉
            </h2>

            {/* Message */}
            <p className="text-gray-600 text-sm mb-6">
              Your GST has been successfully verified. Thank you for completing
              this step.
            </p>

            {/* GST Number Display */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-green-700">
                <ShieldCheck size={18} />
                <span className="font-mono font-semibold text-base">
                  {localStorage.getItem("sellerGstin")}
                </span>
              </div>
            </div>

            {/* Redirect Message */}
            <p className="text-gray-500 text-xs mb-4">
              Redirecting to dashboard in a few seconds...
            </p>

            {/* Spinner */}
            <div className="flex justify-center mb-4">
              <div className="w-8 h-8 border-3 border-green-600 border-t-transparent rounded-full animate-spin"></div>
            </div>

            {/* Manual Redirect Button */}
            <button
              onClick={() => navigate("/")}
              className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center gap-1 mx-auto transition-colors"
            >
              Go to Dashboard Now <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Regular GST verification page
  return (
    <div className="pt-24 pb-16 min-h-screen page-root">
      <div className="max-w-7xl mx-auto px-6">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row items-center gap-6 mb-12 animate-slide-up">
          <div className="w-16 h-16 bg-primary/5 border border-primary/10 rounded-2xl flex items-center justify-center text-primary shadow-xl shadow-primary/10">
            <ShieldCheck size={28} />
          </div>
          <div className="text-center sm:text-left">
            <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">
              GST Verification
            </h1>
            <p className="text-slate-500 text-sm font-medium mt-1">
              Verify your GSTIN to establish business credibility and unlock
              full seller features.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-slide-up">
          {/* ── Form card ── */}
          <div className="lg:col-span-2 inner-card !p-8 flex flex-col gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-600">
                <Search size={20} />
              </div>
              <div>
                <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
                  Verify GSTIN
                </h2>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Real-time GSTN database check
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-primary/5 border border-primary/10 rounded-2xl text-primary">
              <AlertCircle size={18} className="shrink-0 mt-0.5" />
              <p className="text-xs font-medium leading-relaxed">
                Your GSTIN will be verified against the official GSTN database
                in real time.
              </p>
            </div>

            <form onSubmit={handleVerify} className="flex flex-col gap-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                    Registered Email
                  </label>
                  <div className="relative group">
                    <Mail
                      size={16}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
                    />
                    <input
                      id="gst-email"
                      type="email"
                      className="form-input !pl-11"
                      placeholder="seller@example.com"
                      value={form.email}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, email: e.target.value }))
                      }
                    />
                  </div>
                  {errors.email && (
                    <span className="text-[10px] font-medium text-red-500 ml-1">
                      {errors.email}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                    GSTIN Number
                  </label>
                  <div className="relative group">
                    <ShieldCheck
                      size={16}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
                    />
                    <input
                      id="gst-gstin"
                      className="form-input !pl-11 !font-mono !tracking-widest !uppercase"
                      placeholder="27AABPT1653R1Z3"
                      maxLength={15}
                      value={form.gstin}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          gstin: e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9]/g, ""),
                        }))
                      }
                    />
                  </div>
                  {errors.gstin && (
                    <span className="text-[10px] font-medium text-red-500 ml-1">
                      {errors.gstin}
                    </span>
                  )}
                  <p className="text-[10px] font-medium text-slate-400 mt-1 ml-1 uppercase tracking-wider leading-relaxed">
                    2-digit state code + PAN + entity + check digit (15 chars)
                  </p>
                </div>
              </div>

              <button
                id="gst-verify-btn"
                type="submit"
                className="btn btn-primary w-full py-4 text-sm font-bold tracking-wide shadow-xl shadow-primary/20"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Verifying with GSTN...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Search size={18} />
                    <span>Verify GSTIN</span>
                  </div>
                )}
              </button>
            </form>

            {/* ── Result card ── */}
            {result && (
              <div className="p-6 bg-emerald-50 border border-emerald-100 rounded-2xl animate-slide-up">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                    <CheckCircle2 size={20} />
                  </div>
                  <h3 className="text-emerald-700 font-extrabold tracking-tight">
                    Verification Successful
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="flex gap-4">
                    <Building2
                      size={18}
                      className="text-primary shrink-0 mt-1"
                    />
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-1 leading-none">
                        Legal Business Name
                      </div>
                      <div className="text-sm font-extrabold text-slate-900 leading-tight">
                        {result.businessName}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Store size={18} className="text-secondary shrink-0 mt-1" />
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-1 leading-none">
                        Trade Name
                      </div>
                      <div className="text-sm font-extrabold text-slate-900 leading-tight">
                        {result.tradeName}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <ShieldCheck
                      size={18}
                      className={`shrink-0 mt-1 ${
                        result.gstStatus === "Active"
                          ? "text-emerald-500"
                          : "text-red-500"
                      }`}
                    />
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-2 leading-none">
                        GST Status
                      </div>
                      <span
                        className={`badge ${
                          result.gstStatus === "Active"
                            ? "badge-success"
                            : "badge-danger"
                        }`}
                      >
                        {result.gstStatus}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Info sidebar ── */}
          <div className="flex flex-col gap-6">
            <div className="inner-card">
              <h3 className="text-sm font-bold text-slate-900 mb-6 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-primary" />
                Why Verify GST?
              </h3>
              <div className="flex flex-col gap-6">
                {[
                  {
                    title: "Unlock full catalog",
                    desc: "List unlimited products after verification.",
                  },
                  {
                    title: "GST-compliant invoices",
                    desc: "Auto-generated for every order placed.",
                  },
                  {
                    title: "Higher buyer trust",
                    desc: "Verified badge shown on all your listings.",
                  },
                  {
                    title: "Faster payouts",
                    desc: "Reduces manual review time for settlements.",
                  },
                ].map(({ title, desc }) => (
                  <div key={title} className="flex items-start gap-4 group">
                    <div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 shrink-0 mt-0.5">
                      <CheckCircle2 size={12} />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-slate-900 uppercase tracking-widest group-hover:text-primary transition-colors">
                        {title}
                      </div>
                      <div className="text-[10px] font-medium text-slate-500 leading-relaxed mt-1">
                        {desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="inner-card bg-slate-50 border-none">
              <h3 className="text-sm font-bold text-slate-900 mb-6 tracking-tight">
                GSTIN Format Guide
              </h3>
              <div className="flex items-center gap-1 font-mono font-bold text-sm tracking-widest mb-6">
                <span className="text-amber-500">27</span>
                <span className="text-primary">AABPT</span>
                <span className="text-secondary">1653</span>
                <span className="text-cyan-500">R</span>
                <span className="text-orange-500">1Z3</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "State Code", color: "bg-amber-500" },
                  { label: "PAN (5 chars)", color: "bg-primary" },
                  { label: "PAN Entity", color: "bg-secondary" },
                  { label: "PAN Check", color: "bg-cyan-500" },
                  { label: "Check Digit", color: "bg-orange-500" },
                ].map((l) => (
                  <div key={l.label} className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${l.color}`} />
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      {l.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GSTPage;
